import React, { FC } from 'react';
import { useFormik } from 'formik';
import Yup from '~yup';

import { TCreateNewRoleReduxParams } from '~types/feat/admin';

import LabelledInput from '~components/atoms/LabelledInput';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';
import RolePermissions from '../RolesRolePermissions';

import UtilFunctions from '~utils/functions';

import * as FormStyles from './_rolesCreateRoleForm.module.scss';

const RoleCreateRoleFormValidation = Yup.object().shape({
    roleName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().matchNumbersAndAlphabets().required('Role Name is required'),
});

type TRoleCreateRoleForm = {
    submitButtonLabel: string;
    onSubmit: (data: TCreateNewRoleReduxParams) => void;
    error: string;
    actionButtonIsLoading?: boolean;
    actionButtonClassName?: string;
    setError?: (errorString: string) => void;
}

const RoleCreateRoleForm: FC<TRoleCreateRoleForm> = ({
    submitButtonLabel,
    onSubmit,
    error,
    actionButtonIsLoading,
    actionButtonClassName,
    setError,
}) => {
    const { Misc: { blockEnterKey } } = UtilFunctions;

    const RoleCreateRoleFormInitialValues: TCreateNewRoleReduxParams = {
        roleName: '',
        access: [],
        allBranchAccess: false,
    };

    const formik = useFormik({
        initialValues: RoleCreateRoleFormInitialValues,
        validationSchema: RoleCreateRoleFormValidation,
        onSubmit,
    });

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        if (!formik.values.roleName.length && setError) {
            setError('Role Name is required!');
        }

        formik.handleSubmit(e);
    };

    return (
        <form
            onSubmit={handleSubmit}
            className={FormStyles.container}
            onKeyDown={blockEnterKey}
        >
            <div className={FormStyles['form-container']}>
                <div className={FormStyles['form-subcontainer']}>
                    <LabelledInput
                        id='roleName'
                        name='roleName'
                        label='Role Name* :'
                        value={formik.values.roleName}
                        error={formik.touched.roleName ? formik.errors.roleName : undefined}
                        onChange={(e) => {
                            if (error && setError) setError('');

                            formik.handleChange(e);
                        }}
                        onBlur={formik.handleBlur}
                        className={FormStyles['role-name-input']}
                    />

                    <LabelledInput
                        label='All Branches Access: '
                        onChange={() => formik.setFieldValue('allBranchAccess', !formik.values.allBranchAccess)}
                        type='checkbox'
                        checked={formik.values.allBranchAccess}
                        className={FormStyles.checkbox}
                    />
                </div>

                <RolePermissions
                    accessList={formik.values.access}
                    handleChange={formik.handleChange}
                />

                <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                    <div className={FormStyles['button-subcontainer']}>
                        <ActionButton
                            label={submitButtonLabel}
                            isLoading={actionButtonIsLoading}
                            type='submit'
                            disabled={actionButtonIsLoading}
                            className={actionButtonClassName}
                        />

                        <Text
                            label={error}
                            isError
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default RoleCreateRoleForm;
